import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { AppSelect } from '../../../../components/AppSelect';
import type { Material } from '../../types/material';
import {
  calcMaterialVk,
  formatMaterialPrice,
  MATERIAL_UNIT_OPTIONS,
  VAT_OPTIONS,
} from '../../utils/materialForm';
import { parseGermanNumber } from '../../../../utils/parseGermanNumber';

export type MaterialMobileEditOverlayProps = {
  material: Material;
  editDraft: Partial<Material>;
  setEditDraft: React.Dispatch<React.SetStateAction<Partial<Material>>>;
  saving: boolean;
  onClose: () => void;
  onSave: (m: Material, draft: Partial<Material>) => void;
  onDelete: (m: Material) => void;
};

export function MaterialMobileEditOverlay({
  material,
  editDraft,
  setEditDraft,
  saving,
  onClose,
  onSave,
  onDelete,
}: MaterialMobileEditOverlayProps) {
  const draft = { ...material, ...editDraft };
  const editVkPreview = calcMaterialVk(
    draft.net_price != null ? String(draft.net_price).replace('.', ',') : '',
    String(draft.surcharge_percent ?? 0)
  );

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-app-surface">
      <div className="shrink-0 px-4 py-3 border-b border-app-border flex items-center gap-3 bg-app-surface-muted/50">
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 -ml-1 hover:bg-app-hover rounded"
          aria-label="Zurück"
        >
          <ArrowLeft className="w-5 h-5 text-app-text-muted" />
        </button>
        <h2 className="text-base font-semibold text-app-text flex-1 truncate">{material.name}</h2>
        <button
          type="button"
          disabled={saving}
          className="btn-pipedrive-green text-sm py-1.5 px-3 disabled:opacity-50"
          onClick={() => onSave(material, draft)}
        >
          Speichern
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <label className="block text-xs font-medium text-app-text-muted mb-1">Name*</label>
          <input
            value={draft.name}
            onChange={(e) => setEditDraft((p) => ({ ...p, name: e.target.value }))}
            className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-app-text-muted mb-1">Einheit*</label>
            <AppSelect
              value={draft.unit || ''}
              onChange={(v) => setEditDraft((p) => ({ ...p, unit: v || '' }))}
              options={MATERIAL_UNIT_OPTIONS}
              size="sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-app-text-muted mb-1">EK (€)*</label>
            <input
              value={draft.net_price != null ? String(draft.net_price) : ''}
              onChange={(e) =>
                setEditDraft((p) => ({ ...p, net_price: parseGermanNumber(e.target.value) }))
              }
              inputMode="decimal"
              className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-app-text-muted mb-1">Zuschlag (%)*</label>
            <input
              value={draft.surcharge_percent != null ? String(draft.surcharge_percent) : ''}
              onChange={(e) => {
                const v = e.target.value.trim();
                setEditDraft((p) => ({
                  ...p,
                  surcharge_percent: v === '' ? 0 : parseGermanNumber(v),
                }));
              }}
              inputMode="decimal"
              className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-app-text-muted mb-1">MwSt.*</label>
            <AppSelect
              value={String(
                draft.vat_percent === 0 || draft.vat_percent === 7 || draft.vat_percent === 19
                  ? draft.vat_percent
                  : 19
              )}
              onChange={(v) => setEditDraft((p) => ({ ...p, vat_percent: Number(v) }))}
              options={VAT_OPTIONS}
              size="sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-app-text-muted mb-1">Minuten (Einbau)</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={draft.minutes != null ? String(draft.minutes) : ''}
              onChange={(e) => {
                const v = e.target.value.replace(/\D+/g, '');
                setEditDraft((p) => ({ ...p, minutes: v === '' ? null : Number(v) }));
              }}
              className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
              placeholder="z. B. 45"
            />
          </div>
        </div>
        <div className="rounded-lg border border-app-border bg-app-surface-muted px-3 py-2 text-sm text-app-text-muted">
          <span className="font-medium text-app-text-muted">VK (berechnet):</span>{' '}
          <span className="tabular-nums">{formatMaterialPrice(editVkPreview)}</span>
        </div>
        {material.metal_kind && material.metal_kg_per_unit != null ? (
          <div className="rounded-lg border border-app-border bg-app-surface-muted px-3 py-2 text-sm">
            <div className="font-medium text-app-text">
              {material.metal_kind === 'cu' ? 'Kupferanteil' : 'Aluminiumanteil'}
            </div>
            <div className="mt-1 space-y-0.5 text-app-text-muted tabular-nums">
              <div>
                {new Intl.NumberFormat('de-DE', { maximumFractionDigits: 6 }).format(
                  material.metal_kg_per_unit,
                )}{' '}
                kg/{material.unit || 'ME'}
              </div>
              <div>Basis: {formatMaterialPrice(material.metal_basis_eur_per_100kg)} / 100 kg</div>
              <div>Zuschlag: {formatMaterialPrice(material.metal_surcharge)} / {material.unit || 'ME'}</div>
            </div>
          </div>
        ) : null}
        <div>
          <label className="block text-xs font-medium text-app-text-muted mb-1">Marke</label>
          <input
            value={draft.brand || ''}
            onChange={(e) => setEditDraft((p) => ({ ...p, brand: e.target.value }))}
            className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-app-text-muted mb-1">Lieferant</label>
          <input
            value={draft.supplier || ''}
            onChange={(e) => setEditDraft((p) => ({ ...p, supplier: e.target.value }))}
            className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-app-text-muted mb-1">G-Handels Nr.</label>
          <input
            value={draft.gtin || ''}
            onChange={(e) => setEditDraft((p) => ({ ...p, gtin: e.target.value }))}
            className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-app-text-muted mb-1">Beschreibung</label>
          <textarea
            value={draft.description ?? ''}
            onChange={(e) => setEditDraft((p) => ({ ...p, description: e.target.value || null }))}
            rows={3}
            className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none resize-none"
          />
        </div>
        {material.kind !== 'shared' && (
          <div className="pt-2 border-t border-app-border">
            <button
              type="button"
              className="w-full py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              onClick={() => {
                if (!confirm('Material wirklich löschen?')) return;
                onDelete(material);
              }}
            >
              Material löschen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
